import React, { useMemo, useState } from 'react';
import Button from '../Button';
import Pagination from '../Pagination';
import { Menu, MenuForm, MenuItem, MenuSeparator } from '../Menu';
import {
  mileageMessage as m, monthColumns, monthRows, monthSortColumns,
} from '../../domain/mileage';
import { useLocale, useMonthFormat } from '../../context/LocaleContext';
import s from './Mileage.module.css';

// "Trips in October 2026" (#521): one month's trips, as MMS draws the page a
// month row opens:
//
//   Back to Mileage Log Overview
//   Trips in October 2026
//   + Add Mileage                          Columns ▾  Sort ▾  Search ▾
//   Date ↓ | Destination | Distance (mi) | Odometer | ⋮
//
// the date written long ("Friday, 30 October 2026") with the "Repeating
// Trip" tag under it, an empty odometer `-`, and ⋮ holding Edit and Delete;
// 25 to a page. Columns ▾ offers Show All, Show Default and each column;
// Sort ▾ Clear Sort, Date, Destination, Distance and Odometer; Search ▾ a
// box and "Only show repeating trips", with Clear and Search. The rows, the
// sort and the search are core/src/mileage.rs's (`month_rows`), the menus'
// entries too (`month_columns`, `month_sort_columns`); this draws them.
//
// The Distance column is headed with the studio's unit and holds the bare
// number, as MMS's does — except for a trip logged in the other unit before a
// Region change, which keeps its own unit and says so in the cell
// (`distance_figure`).
//
// Edit and Delete act on the row's date (#554): `onEdit(row)` and
// `onDelete(row)` hand the row — its trip and its date — to the page.
const DEFAULT_SORT = { key: '', desc: false };
const BLANK_SEARCH = { text: '', onlyRepeating: false };

export default function MileageTable({ trips, unit, today, month, onBack, onAdd, onEdit, onDelete }) {
  const monthName = useMonthFormat();
  const { shownDecimal, formatLongDate } = useLocale();
  const columns = useMemo(() => monthColumns(unit), [unit]);
  const sorts = useMemo(() => monthSortColumns(unit), [unit]);
  const [shown, setShown] = useState(() => columns.filter((c) => c.shown).map((c) => c.key));
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [search, setSearch] = useState(BLANK_SEARCH);
  const [draft, setDraft] = useState(BLANK_SEARCH);
  const [menu, setMenu] = useState(null); // null | 'columns' | 'sort' | 'search' | a row id
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const rows = useMemo(
    () => monthRows(trips, unit, month, today, { sort: sort.key, desc: sort.desc, ...search }),
    [trips, unit, month, today, sort, search],
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  const current = Math.min(page, pageCount);
  const visible = rows.slice((current - 1) * perPage, current * perPage);
  const close = () => setMenu(null);
  const toggle = (which) => setMenu((cur) => (cur === which ? null : which));
  const visibleColumns = columns.filter((c) => shown.includes(c.key));
  // A sort column is chosen once ascending, again descending, as the
  // Expenses page's Sort ▾ does.
  const sortBy = (key) => setSort((cur) => (cur.key === key ? { key, desc: !cur.desc } : { key, desc: false }));
  const arrow = (key) => (sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '');
  const dateArrow = sort.key === '' ? ' ↓' : arrow('occurredOn');

  const cell = (r, key) => {
    switch (key) {
      case 'destination': return r.destination || '-';
      case 'distance': return shownDecimal(r.distanceFigure) || '-';
      case 'duration': return r.durationClock || '-';
      case 'notes': return r.notes || '-';
      case 'odometer': return shownDecimal(r.odometer) || '-';
      case 'vehicle': return r.vehicle || '-';
      default: return '-';
    }
  };

  return (
    <>
      <div>
        <button type="button" className={s.linkButton} onClick={onBack}>‹ {m('backToOverview')}</button>
        <h2 className={s.monthTitle}>{m('tripsIn')} {monthName(month)}</h2>
      </div>

      <div className={s.toolbar}>
        <Button onClick={onAdd}>{m('addMileage')}</Button>
        <span className={s.spacer} />

        <Menu
          open={menu === 'columns'}
          onClose={close}
          trigger={<Button variant="secondary" onClick={() => toggle('columns')}>Columns ▾</Button>}
        >
          <MenuItem accent onClick={() => setShown(columns.map((c) => c.key))}>Show All</MenuItem>
          <MenuItem accent onClick={() => setShown(columns.filter((c) => c.shown).map((c) => c.key))}>Show Default</MenuItem>
          <MenuSeparator />
          {columns.map((c) => (
            <label key={c.key} className={s.menuCheck}>
              <input
                type="checkbox"
                checked={shown.includes(c.key)}
                onChange={(e) => setShown((cur) => (e.target.checked ? [...cur, c.key] : cur.filter((k) => k !== c.key)))}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </Menu>

        <Menu
          open={menu === 'sort'}
          onClose={close}
          trigger={<Button variant="secondary" onClick={() => toggle('sort')}>Sort ▾</Button>}
        >
          <MenuItem accent onClick={() => { setSort(DEFAULT_SORT); close(); }}>Clear Sort</MenuItem>
          <MenuSeparator />
          {sorts.map((c) => (
            <MenuItem key={c.key} onClick={() => { sortBy(c.key); close(); }}>{c.label}{arrow(c.key)}</MenuItem>
          ))}
        </Menu>

        <MenuForm
          open={menu === 'search'}
          onClose={close}
          trigger={<Button variant="secondary" onClick={() => { setDraft(search); toggle('search'); }}>Search ▾</Button>}
        >
          <input
            className={s.searchField}
            placeholder={m('searchPlaceholder')}
            value={draft.text}
            onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          />
          <label className={s.menuCheck}>
            <input
              type="checkbox"
              checked={draft.onlyRepeating}
              onChange={(e) => setDraft({ ...draft, onlyRepeating: e.target.checked })}
            />
            <span>{m('onlyRepeating')}</span>
          </label>
          <div className={s.searchActions}>
            <Button variant="secondary" onClick={() => { setSearch(BLANK_SEARCH); setDraft(BLANK_SEARCH); setPage(1); close(); }}>Clear</Button>
            <Button onClick={() => { setSearch(draft); setPage(1); close(); }}>Search</Button>
          </div>
        </MenuForm>
      </div>

      <div className={s.panel}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>{m('columnDate')}{dateArrow}</th>
              {visibleColumns.map((c) => <th key={c.key}>{c.label}</th>)}
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id}>
                <td className={s.number}>
                  {formatLongDate(r.occurredOn)}
                  {r.series ? <span className={s.tagBelow}>{m('recurringTag')}</span> : null}
                </td>
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.key === 'notes' ? s.notes : s.number}>{cell(r, c.key)}</td>
                ))}
                <td className={s.rowMenuCell}>
                  <Menu
                    open={menu === r.id}
                    onClose={close}
                    trigger={(
                      <button className={s.kebab} aria-label="Row actions" onClick={() => toggle(r.id)}>⋮</button>
                    )}
                  >
                    <MenuItem icon="pencil" onClick={() => { close(); onEdit(r); }}>{m('edit')}</MenuItem>
                    <MenuItem icon="trash" onClick={() => { close(); onDelete(r); }}>{m('delete')}</MenuItem>
                  </Menu>
                </td>
              </tr>
            ))}
            {rows.length ? null : (
              <tr><td colSpan={visibleColumns.length + 2} className={s.none}>–</td></tr>
            )}
          </tbody>
        </table>
        <Pagination
          page={current}
          pageCount={pageCount}
          perPage={perPage}
          total={rows.length}
          onPageChange={setPage}
          onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
        />
      </div>
    </>
  );
}
